import styled, { css, type DefaultTheme } from "styled-components";
import { mobileOnly, tabletOnly, mobileAndTablet } from "_/style/media";
import { desaturate, readableColor } from "polished";
import { Icon } from "_/components/icon";

export const Wrapper = styled.div`
  display: flex;
  position: relative;
  height: 100%;
`;

/* Top bar only visible on tablet & mobile */
export const MobileTopBar = styled.div`
  position: sticky;
  top: 0;
  display: none;
  align-items: center;
  gap: 0.75rem;
  min-height: 3.125rem;
  padding: 0 0.75rem;
  background: white;
  color: black;
  border-bottom: 1px solid gray;

  a {
    color: ${(p) => p.theme.color.brand.blue};
    text-decoration: underline;
  }

  ${mobileAndTablet`
    display: flex;
  `}
`;

export const DocsNavList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

// Docs nav active-item accent. Acid green reads with high energy on the dark
// sidebar, but is illegible (~1.1:1) on light backgrounds, so fall back to the
// standard brand-blue link accent when the light theme is active.
const ACTIVE_ACCENT_DARK = "#CBFF3D";

const navItemColor = (p: {
  $depth: number;
  $parent: boolean;
  $active: boolean;
  theme: DefaultTheme;
}): string => {
  if (p.$active)
    return p.theme.dark ? ACTIVE_ACCENT_DARK : p.theme.link.default; // current page
  if (p.$depth === 0 && p.$parent) return p.theme.text.secondary; // section
  return p.theme.text.muted; // idle child — recedes
};

export const DocsNavItem = styled.li<{
  $depth: number;
  $active: boolean;
  $parent: boolean;
}>`
  position: relative;
  margin-left: ${(p) => p.$depth}rem;
  font-size: ${(p) => (p.$depth === 0 && p.$parent ? "1rem" : "0.875rem")};
  font-weight: ${(p) => (p.$depth === 0 && p.$parent ? 500 : 400)};
  color: ${navItemColor};
  display: flex;
  align-items: center;

  & a {
    padding: 3px 0;
    line-height: 1.03125rem;
    transition: 0.05s color;

    &:hover {
      color: ${(p) => p.theme.text.default};
    }
  }
`;

export const DocsNavItemArrow = styled.div<{ $expanded: boolean }>`
  display: inline-block;
  position: absolute;
  cursor: pointer;

  width: 0;
  height: 0;

  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid
    ${(p) => (p.$expanded ? p.theme.text.default : p.theme.text.secondary)};

  left: -1em;

  transition: transform 0.15s ease-in-out;
  transform: rotate(${(p) => (p.$expanded ? "0deg" : "-90deg")});
`;

export const MainContent = styled.div<{ $open?: boolean }>`
  padding: 20px 60px 200px;
  color: ${(p) => p.theme.text.default};
  background-color: ${(p) => p.theme.background.primary};
  width: 100%;
  overflow-y: scroll;
  max-height: 100%;
  scroll-behavior: smooth;
  overscroll-behavior-y: contain;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: 500;

    a {
      color: inherit;
    }

    // Currently we only use H1 and H2 headings. If we end up using more, then
    // we'll need to adjust the margins accordingly.
    &:not(:first-child) {
      margin-top: 1.75em;
      margin-bottom: 0.75rem;
    }

    &:target {
      position: relative;

      &::before {
        content: "⏵";
        display: block;
        position: absolute;
        left: -1em;
      }
    }
  }

  li::marker {
    font-family: monospace;
    margin-right: 0.25em;
  }

  li {
    padding-top: 0.375rem;
  }

  p + p {
    padding-top: 0.75rem;
  }

  code {
    margin: 0;
    color: ${(p) => p.theme.text.secondary};
    font-family: monospace;
    font-size: 95%;
    background-color: ${(p) => p.theme.background.secondary};
    padding: 0.1em 0.4em;
    border-radius: 2px;
    position: relative;
    top: -0.1em;
  }

  @media print {
    code {
      background-color: transparent;
      border: 1px solid #eee;
    }
  }

  pre {
    border-radius: 6px;
    overflow: hidden;

    code {
      background-color: transparent;
      display: block;

      * {
        font-family: inherit;
      }
    }
  }

  a {
    color: ${(p) => p.theme.color.brand.blue};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  ${mobileAndTablet`
    padding: 20px 16px 160px;
  `}

  @media print {
    background-color: white;
    color: black;

    table,
    pre,
    blockquote {
      page-break-inside: avoid !important;
    }
  }
`;

export const CopyCode = styled.span`
  display: block;
  position: absolute;
  right: 1em;
  top: 1em;
  padding: 8px;
  border-radius: 4px;

  background-color: ${(p) => p.theme.background.secondary};

  &:hover {
    cursor: pointer;
    background-color: ${(p) => p.theme.background.tertiary};
  }

  &:active {
    background-color: ${(p) => p.theme.background.quaternary};
  }
`;

export const MdxContentWrapper = styled.div`
  display: flex;
  width: 100%;
  max-height: 100%;
`;

// Non-scrolling container around the doc content, which anchors the draft
// ribbon so that it stays in place while the content scrolls.
export const MainColumn = styled.div`
  display: flex;
  position: relative;
  overflow: hidden;
  flex: 1;
  min-width: 0;
`;

// Diagonal banner across the top right corner of draft docs.
export const DraftRibbon = styled.div`
  position: absolute;
  top: 40px;
  right: -60px;
  width: 240px;
  padding: 8px 0;
  z-index: 1;
  pointer-events: none;

  transform: rotate(45deg);
  background-color: ${(p) => desaturate(0.4, p.theme.toast.danger)};
  color: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);

  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: 0.25em;
  text-align: center;
  text-transform: uppercase;
`;

export const ModifiedAt = styled.div`
  margin-top: 2em;
  color: ${(p) => p.theme.text.muted};
  font-size: 0.8em;
`;

export const TocContent = styled.div`
  padding: 20px;
  color: ${(p) => p.theme.text.default};
  background-color: ${(p) => p.theme.background.primary};
  flex: 0 0 250px;
  height: 100%;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  scroll-behavior: smooth;

  h3 {
    margin: 0;
  }

  @media screen and (max-width: 1400px) {
    display: none;
  }
`;

export const PageToc = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const PageTocItem = styled.li<{ $depth: number }>`
  padding: 0;
  margin-left: ${(p) => p.$depth}em;
  font-size: 14px;
  color: ${(p) => p.theme.text.secondary};

  & a {
    display: inline-block;
    padding: 6px 0;
    line-height: 1.03125rem;
    transition: 0.05s color;

    &:hover {
      color: ${(p) => p.theme.text.default};
    }
  }
`;

export const DocsHr = styled.hr`
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 2px;
`;

export const DocsImg = styled.img`
  max-width: 100%;
`;

export const Notice = styled.div<{ $bgColor: string }>`
  padding: 1.5em 2em;
  margin: 1.6em 0;
  border-radius: 6px;
  background-color: ${(p) => p.$bgColor};
  color: ${(p) =>
    readableColor(
      p.$bgColor,
      p.theme.color.palette.almostBlack,
      p.theme.color.palette.snowWhite
    )};

  a {
    text-decoration: underline;
    color: ${(p) =>
      readableColor(
        p.$bgColor,
        p.theme.color.palette.electricBlue,
        p.theme.color.palette.twilightBlue
      )};
  }

  @media print {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    page-break-inside: avoid;
  }
`;

export const NoticeHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1em;

  ${Icon} {
    font-size: 1.5em;
  }
`;

export const NoticeTitle = styled.span`
  margin: 0 0 0 0.5em;
  font-size: 1.25em;
`;

export const TabsWrapper = styled.div`
  margin: 1.6em 0;
  background-color: ${(p) => p.theme.background.secondary};
  border: 1px solid ${(p) => p.theme.border.default};
  border-radius: 8px;
  overflow: hidden;

  @media print {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    page-break-inside: avoid;
  }
`;

export const TabBar = styled.div`
  display: flex;
  gap: 0.25em;
  padding: 0.35em 0.5em 0;
  /* Recessed strip so the raised active tab (panel-coloured) stands out. */
  background-color: ${(p) => p.theme.background.primary};
  border-bottom: 1px solid ${(p) => p.theme.border.default};

  @media print {
    display: none;
  }
`;

// Right-aligned "how do I identify my version?" help icon in the tab bar. The
// icon links to the identification page; a hover tooltip explains the target.
export const TabBarHelp = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;

  a {
    display: inline-flex;
    align-items: center;
    /* Icon glyphs size to the inherited font-size. */
    font-size: 1.125rem;
    color: ${(p) => p.theme.text.secondary};
    text-decoration: none;
    transition: color 0.1s ease;
  }

  a:hover {
    color: ${(p) => p.theme.text.default};
  }
`;

const tabButtonColor = (p: {
  theme: DefaultTheme;
  $active: boolean;
  $crossedOut: boolean;
}) => {
  if (p.$crossedOut) return p.theme.text.muted;
  return p.$active ? p.theme.text.default : p.theme.text.secondary;
};

// Radius of the active tab's rounded top corners.
const TAB_CORNER_RADIUS = "6px";

// Radius of the bottom fillets. Larger than the top corners (the usual
// browser-tab proportion) so the flare into the panel is clearly visible.
const TAB_FILLET_RADIUS = "20px";

// Concave "fillet" corners that flare the active tab's bottom edge outward into
// the content panel, so tab and panel read as one continuous surface (the
// browser-tab look). Each fillet is a small square sitting just outside a bottom
// corner, filled with the panel colour except for a quarter-circle carved out of
// the inner corner by the radial gradient — that carved curve is the fillet.
const tabFillets = (p: { theme: DefaultTheme }) => css`
  /* Raise the active tab above its siblings so the fillets, which overflow into
     the adjacent tabs' edges, are not painted over. Sibling tabs to the right
     come later in the DOM and would otherwise cover the ::after fillet with
     their hover background. */
  z-index: 1;

  &::before,
  &::after {
    content: "";
    position: absolute;
    /* Align with the tab's border-box bottom (1px below the padding box, since
       offsets are relative to the padding edge), which is pulled onto the panel
       surface by the tab's -1px bottom margin. */
    bottom: -1px;
    width: ${TAB_FILLET_RADIUS};
    height: ${TAB_FILLET_RADIUS};
  }
  &::before {
    left: -${TAB_FILLET_RADIUS};
    background: radial-gradient(
      circle at top left,
      transparent ${TAB_FILLET_RADIUS},
      ${p.theme.background.secondary} ${TAB_FILLET_RADIUS}
    );
  }
  &::after {
    right: -${TAB_FILLET_RADIUS};
    background: radial-gradient(
      circle at top right,
      transparent ${TAB_FILLET_RADIUS},
      ${p.theme.background.secondary} ${TAB_FILLET_RADIUS}
    );
  }
`;

export const TabButton = styled.button<{
  $active: boolean;
  $crossedOut: boolean;
}>`
  appearance: none;
  box-sizing: border-box;
  position: relative;
  /* Reserve the active tab's border on every tab so selecting one does not
     shift the row. The active tab gets a blue top accent and side borders that
     match the panel; its bottom border is painted the panel colour and pulled
     down 1px to cover the bar's separator, merging tab and content into one
     surface (the browser-tab look). The bottom corners flare outward into the
     panel via the fillet pseudo-elements (see tabFillets). */
  border: 1px solid transparent;
  border-top-width: 2px;
  border-top-left-radius: ${TAB_CORNER_RADIUS};
  border-top-right-radius: ${TAB_CORNER_RADIUS};
  margin-bottom: -1px;
  padding: 0.5em 0.9em;
  font: inherit;
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? 500 : 400)};
  cursor: pointer;
  background: ${(p) =>
    p.$active ? p.theme.background.secondary : "transparent"};
  border-color: ${(p) => (p.$active ? p.theme.border.default : "transparent")};
  border-top-color: ${(p) =>
    p.$active ? p.theme.color.brand.blue : "transparent"};
  border-bottom-color: ${(p) =>
    p.$active ? p.theme.background.secondary : "transparent"};
  color: ${tabButtonColor};
  text-decoration: ${(p) => (p.$crossedOut ? "line-through" : "none")};
  transition:
    color 0.1s ease,
    background-color 0.1s ease;

  &:hover {
    color: ${(p) =>
      p.$crossedOut ? p.theme.text.muted : p.theme.text.default};
    background: ${(p) =>
      p.$active ? p.theme.background.secondary : p.theme.background.tertiary};
  }

  ${(p) => p.$active && tabFillets(p)}
`;

export const TabPanel = styled.div`
  padding: 0.5em 1em 1em;
`;

// Shown in a declared-axis block when the selected value has no applicable tab.
export const TabNotApplicable = styled.div`
  color: ${(p) => p.theme.text.muted};
  font-style: italic;
`;

// Replaces the interactive tab bar when printing, so the printout records which
// tab's content is shown.
export const TabPrintLabel = styled.div`
  display: none;

  @media print {
    display: block;
    font-weight: 500;
    margin: 0.5em 0;
  }
`;

export const LoadingWrapper = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;

  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  align-items: center;
  justify-content: center;
`;

export const LoadingSpinner = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 100%;
  border: 2px solid ${(p) => p.theme.color.brand.blue};

  animation: spin ease-in 0.8s alternate infinite;

  @keyframes spin {
    0% {
    }
    100% {
      width: 4px;
      height: 4px;
    }
  }
`;

export const ContentsListing = styled.div`
  padding: 1.2em 2em;
  margin: 3em 0;
  border-radius: 6px;
  background-color: ${(p) => p.theme.background.secondary};

  @media print {
    background-color: transparent;
    border: 1px solid #eee;
    page-break-inside: avoid;
  }
`;

export const EditLink = styled.a`
  color: ${(p) => p.theme.link.default};
  display: flex;
  align-items: center;
  gap: 0.2em;
  font-size: 0.8em;
`;

export const PageArrows = styled.div`
  pointer-events: none;
  z-index: 20;
  svg {
    width: 2rem;
    height: 2rem;
  }
`;

const ArrowBase = styled.div`
  position: fixed;
  pointer-events: auto;
  top: auto;
  bottom: calc(
    var(--footer-height, 0px) + env(safe-area-inset-bottom, 0px) + 1rem
  );

  a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    border-radius: 9999px;
    background: ${(p) => p.theme.color.palette.concreteGrey};
    color: ${(p) => p.theme.color.palette.pebbleGrey};
    border: 2px solid ${(p) => p.theme.border};
    box-shadow: rgba(0, 0, 0, 0.25) 0px 1px 6px;
    transition:
      transform 0.12s ease,
      background 0.12s ease;
  }
  a:hover {
    transform: scale(1.04);
  }
  a:active {
    transform: scale(0.98);
  }
`;

export const PageArrowLeft = styled(ArrowBase)`
  left: 395px;
  ${tabletOnly`
    left: calc(80px + 0.5rem);
  `}

  ${mobileOnly` 
    left: 1rem;
  `}
`;

export const PageArrowRight = styled(ArrowBase)`
  /* DESKTOP default when TOC hidden */
  right: 1rem;

  /* DESKTOP wide: nudge away from 300px right TOC */
  @media (min-width: 1400px) {
    right: 270px;
  }

  ${mobileAndTablet` 
    right: 1rem;
  `}
`;
